export type UnitConversion = {
	fromUnit: string;
	toUnit: string;
	factor: string;
	consumableId?: number;
};

export type ConsumableCustody = "internal" | "external";

export type ConsumableStockLocation = {
	custody: ConsumableCustody;
	customerId: string;
	siteId: string;
};

export type ConsumableBalanceRow = {
	id?: number;
	consumableId: number;
	custody: ConsumableCustody;
	customerId: string;
	siteId: string;
	qty: string;
};

export type ConsumableRow = {
	id?: number;
	unit: string;
	qty: string;
	location?: ConsumableCustody;
	customerId?: string;
	siteId?: string;
};

export type ConsumableMovementRow = {
	id?: number;
	consumableId: number;
	type: "in" | "out" | "transfer";
	qty: string;
	unit: string;
	at: string;
	note?: string;
	custody?: ConsumableCustody;
	customerId?: string;
	siteId?: string;
	fromCustody?: ConsumableCustody;
	fromCustomerId?: string;
	fromSiteId?: string;
	toCustody?: ConsumableCustody;
	toCustomerId?: string;
	toSiteId?: string;
	fromLocation?: string;
	toLocation?: string;
	/** Set when the parent consumable was deleted; excluded from stock reconciliation. */
	consumableArchived?: boolean;
	/** Stock out issued from On Hands for use at customer/site (not With Client balance). */
	issuedUsage?: boolean;
};

export const CONSUMABLE_ON_HANDS: ConsumableStockLocation = {
	custody: "internal",
	customerId: "",
	siteId: "",
};

export function parseQty(value: string): number {
	const parsed = Number(String(value ?? "").trim());
	return Number.isFinite(parsed) ? parsed : 0;
}

export function formatQty(value: number): string {
	if (!Number.isFinite(value)) return "0";
	const rounded = Math.round(value * 1000) / 1000;
	return String(rounded);
}

export function convertQtyToUnit(
	qty: number,
	fromUnit: string,
	toUnit: string,
	conversions: UnitConversion[],
	consumableId?: number,
): number {
	const from = fromUnit.trim().toLowerCase();
	const to = toUnit.trim().toLowerCase();
	if (!from || !to || from === to) return qty;
	const relevant = conversions.filter((row) => !row.consumableId || row.consumableId === consumableId);
	for (const row of relevant) {
		const rowFrom = row.fromUnit.trim().toLowerCase();
		const rowTo = row.toUnit.trim().toLowerCase();
		const factor = Number(row.factor);
		if (!Number.isFinite(factor) || factor <= 0) continue;
		if (rowFrom === from && rowTo === to) return qty * factor;
		if (rowFrom === to && rowTo === from) return qty / factor;
	}
	return qty;
}

/** Form values and legacy records sometimes use labels instead of canonical type ids. */
export function normalizeMovementType(raw: unknown): "in" | "out" | "transfer" {
	const value = String(raw ?? "").trim().toLowerCase();
	if (value === "out" || value === "stock out" || value === "stock-out") return "out";
	if (value === "transfer" || value === "stock transfer" || value === "stock-transfer") return "transfer";
	if (value === "in" || value === "stock in" || value === "stock-in") return "in";
	return "in";
}

export function consumableLocationKey(loc: ConsumableStockLocation): string {
	return `${loc.custody}|${loc.customerId}|${loc.siteId}`;
}

export function normalizeConsumableLocation(
	custodyRaw: string,
	customerIdRaw: string,
	siteIdRaw: string,
): ConsumableStockLocation {
	const custody: ConsumableCustody = custodyRaw === "external" ? "external" : "internal";
	if (custody === "internal") {
		return { custody: "internal", customerId: "", siteId: "" };
	}
	return {
		custody: "external",
		customerId: customerIdRaw.trim(),
		siteId: siteIdRaw.trim(),
	};
}

export function legacyLocationFromConsumable(row: Pick<ConsumableRow, "location" | "customerId" | "siteId">): ConsumableStockLocation {
	const custody: ConsumableCustody = row.location === "external" ? "external" : "internal";
	if (custody === "internal") {
		return { custody: "internal", customerId: "", siteId: "" };
	}
	const customerId = (row.customerId ?? "").trim();
	const siteId = (row.siteId ?? "").trim();
	if (!customerId || !siteId) {
		return { custody: "internal", customerId: "", siteId: "" };
	}
	return { custody: "external", customerId, siteId };
}

export function balancesForConsumable(balances: ConsumableBalanceRow[], consumableId: number): ConsumableBalanceRow[] {
	return balances.filter((row) => row.consumableId === consumableId);
}

export function consumableHasCustody(
	balances: ConsumableBalanceRow[],
	consumableId: number,
	custody: ConsumableCustody,
): boolean {
	return balancesForConsumable(balances, consumableId).some((row) => {
		if (row.custody !== custody) return false;
		return parseQty(row.qty) > 0;
	});
}

function findBalanceIndex(
	balances: ConsumableBalanceRow[],
	consumableId: number,
	loc: ConsumableStockLocation,
): number {
	const key = consumableLocationKey(loc);
	return balances.findIndex(
		(row) => row.consumableId === consumableId && consumableLocationKey(row) === key,
	);
}

export function balanceQtyAt(
	balances: ConsumableBalanceRow[],
	consumableId: number,
	loc: ConsumableStockLocation,
): number {
	const index = findBalanceIndex(balances, consumableId, loc);
	if (index < 0) return 0;
	return parseQty(balances[index]?.qty ?? "0");
}

export type BalancePatch = {
	id?: number;
	consumableId: number;
	loc: ConsumableStockLocation;
	qty: string;
	delete?: boolean;
};

export function applyBalanceDelta(
	balances: ConsumableBalanceRow[],
	consumableId: number,
	loc: ConsumableStockLocation,
	deltaPrimary: number,
): { balances: ConsumableBalanceRow[]; patches: BalancePatch[] } {
	const next = [...balances];
	const index = findBalanceIndex(next, consumableId, loc);
	const current = index >= 0 ? parseQty(next[index]?.qty ?? "0") : 0;
	const updated = current + deltaPrimary;
	if (updated < -1e-9) {
		throw new Error("Not enough stock at that location.");
	}
	const patches: BalancePatch[] = [];
	if (updated <= 1e-9) {
		if (index >= 0) {
			const row = next[index]!;
			patches.push({ id: row.id, consumableId, loc, qty: "0", delete: true });
			next.splice(index, 1);
		}
		return { balances: next, patches };
	}
	const qty = formatQty(updated);
	if (index >= 0) {
		const row = next[index]!;
		next[index] = { ...row, qty };
		patches.push({ id: row.id, consumableId, loc, qty });
	} else {
		const row: ConsumableBalanceRow = { consumableId, ...loc, qty };
		next.push(row);
		patches.push({ consumableId, loc, qty });
	}
	return { balances: next, patches };
}

export type MovementInput = {
	type: "in" | "out" | "transfer";
	qty: string;
	unit: string;
	at: string;
	note: string;
	loc?: ConsumableStockLocation;
	/** Stock out: customer/site where materials were issued (source is always On Hands). */
	usage?: Pick<ConsumableStockLocation, "customerId" | "siteId">;
	from?: ConsumableStockLocation;
	to?: ConsumableStockLocation;
};

function isUsageStockOut(movement: ConsumableMovementRow): boolean {
	if (movement.type !== "out") return false;
	if (movement.issuedUsage) return true;
	return movement.custody === "internal" && Boolean((movement.customerId ?? "").trim() && (movement.siteId ?? "").trim());
}

export function collapseLegacyExternalBalances(balances: ConsumableBalanceRow[]): ConsumableBalanceRow[] {
	const byConsumable = new Map<number, ConsumableBalanceRow[]>();
	for (const row of balances) {
		const list = byConsumable.get(row.consumableId) ?? [];
		list.push(row);
		byConsumable.set(row.consumableId, list);
	}
	const next: ConsumableBalanceRow[] = [];
	for (const [consumableId, rows] of byConsumable) {
		let internal = rows.find((row) => row.custody === "internal");
		let internalQty = internal ? parseQty(internal.qty) : 0;
		for (const row of rows) {
			if (row.custody === "external") internalQty += parseQty(row.qty);
		}
		if (internalQty > 0) {
			next.push({
				...(internal ?? { consumableId, custody: "internal", customerId: "", siteId: "" }),
				consumableId,
				custody: "internal",
				customerId: "",
				siteId: "",
				qty: formatQty(internalQty),
			});
		}
	}
	return next;
}

export function movementDeltaPrimary(
	input: Pick<MovementInput, "qty" | "unit">,
	primaryUnit: string,
	conversions: UnitConversion[],
	consumableId: number | undefined,
): number {
	return convertQtyToUnit(parseQty(input.qty), input.unit, primaryUnit, conversions, consumableId);
}

export function movementRecordToInput(movement: ConsumableMovementRow): MovementInput {
	const type = normalizeMovementType(movement.type);
	const base = {
		type,
		qty: String(movement.qty ?? ""),
		unit: String(movement.unit ?? ""),
		at: String(movement.at ?? ""),
		note: String(movement.note ?? ""),
	};
	if (type === "transfer") {
		return {
			...base,
			from: normalizeConsumableLocation(
				movement.fromCustody ?? movement.fromLocation ?? "internal",
				movement.fromCustomerId ?? "",
				movement.fromSiteId ?? "",
			),
			to: normalizeConsumableLocation(
				movement.toCustody ?? movement.toLocation ?? "internal",
				movement.toCustomerId ?? movement.customerId ?? "",
				movement.toSiteId ?? movement.siteId ?? "",
			),
		};
	}
	if (type === "out" && isUsageStockOut(movement)) {
		return {
			...base,
			usage: {
				customerId: String(movement.customerId ?? "").trim(),
				siteId: String(movement.siteId ?? "").trim(),
			},
		};
	}
	return {
		...base,
		loc: normalizeConsumableLocation(
			movement.custody ?? movement.fromLocation ?? "internal",
			movement.customerId ?? "",
			movement.siteId ?? "",
		),
	};
}

export function applyConsumableMovement(
	balances: ConsumableBalanceRow[],
	consumable: ConsumableRow,
	conversions: UnitConversion[],
	input: MovementInput,
): { balances: ConsumableBalanceRow[]; patches: BalancePatch[]; movement: ConsumableMovementRow } {
	if (consumable.id == null) throw new Error("Save the consumable before recording stock.");
	const type = normalizeMovementType(input.type);
	const normalizedInput: MovementInput = { ...input, type };
	const delta = movementDeltaPrimary(normalizedInput, consumable.unit, conversions, consumable.id);
	if (delta <= 0) throw new Error("Enter a quantity greater than zero.");

	let working = balances;
	const patches: BalancePatch[] = [];

	if (type === "in") {
		const dest = CONSUMABLE_ON_HANDS;
		const result = applyBalanceDelta(working, consumable.id, dest, delta);
		working = result.balances;
		patches.push(...result.patches);
	} else if (type === "out") {
		if (normalizedInput.usage) {
			const customerId = normalizedInput.usage.customerId.trim();
			const siteId = normalizedInput.usage.siteId.trim();
			if (!customerId || !siteId) throw new Error("Choose customer and site for this issue.");
			const available = balanceQtyAt(working, consumable.id, CONSUMABLE_ON_HANDS);
			if (delta > available + 1e-9) throw new Error("Not enough stock on hands.");
			const result = applyBalanceDelta(working, consumable.id, CONSUMABLE_ON_HANDS, -delta);
			working = result.balances;
			patches.push(...result.patches);
		} else {
			if (!normalizedInput.loc) throw new Error("Choose custody for this stock out.");
			const available = balanceQtyAt(working, consumable.id, normalizedInput.loc);
			if (delta > available + 1e-9) throw new Error("Not enough stock at that location.");
			const result = applyBalanceDelta(working, consumable.id, normalizedInput.loc, -delta);
			working = result.balances;
			patches.push(...result.patches);
		}
	} else if (type === "transfer") {
		if (!normalizedInput.from || !normalizedInput.to) throw new Error("Choose from and to locations.");
		if (normalizedInput.from.custody === "external" && (!normalizedInput.from.customerId || !normalizedInput.from.siteId)) {
			throw new Error("Choose from customer and site.");
		}
		if (normalizedInput.to.custody === "external" && (!normalizedInput.to.customerId || !normalizedInput.to.siteId)) {
			throw new Error("Choose to customer and site.");
		}
		const available = balanceQtyAt(working, consumable.id, normalizedInput.from);
		if (delta > available + 1e-9) throw new Error("Not enough stock at the source location.");
		const outResult = applyBalanceDelta(working, consumable.id, normalizedInput.from, -delta);
		working = outResult.balances;
		patches.push(...outResult.patches);
		const inResult = applyBalanceDelta(working, consumable.id, normalizedInput.to, delta);
		working = inResult.balances;
		patches.push(...inResult.patches);
	} else {
		throw new Error("Unknown stock movement type.");
	}

	const movement: ConsumableMovementRow = {
		consumableId: consumable.id,
		type,
		qty: normalizedInput.qty,
		unit: normalizedInput.unit,
		at: normalizedInput.at,
		note: normalizedInput.note,
		custody:
			type === "in"
				? "internal"
				: type === "out" && normalizedInput.usage
					? "internal"
					: normalizedInput.loc?.custody ?? normalizedInput.from?.custody,
		customerId:
			type === "out" && normalizedInput.usage
				? normalizedInput.usage.customerId.trim()
				: type === "in"
					? ""
					: normalizedInput.loc?.customerId ?? normalizedInput.from?.customerId,
		siteId:
			type === "out" && normalizedInput.usage
				? normalizedInput.usage.siteId.trim()
				: type === "in"
					? ""
					: normalizedInput.loc?.siteId ?? normalizedInput.from?.siteId,
		issuedUsage: type === "out" && normalizedInput.usage ? true : undefined,
		fromCustody: normalizedInput.from?.custody,
		fromCustomerId: normalizedInput.from?.customerId,
		fromSiteId: normalizedInput.from?.siteId,
		toCustody: type === "transfer" ? normalizedInput.to?.custody : undefined,
		toCustomerId: type === "transfer" ? normalizedInput.to?.customerId : undefined,
		toSiteId: type === "transfer" ? normalizedInput.to?.siteId : undefined,
		fromLocation: normalizedInput.from?.custody,
		toLocation: type === "transfer" ? normalizedInput.to?.custody : undefined,
	};

	return { balances: working, patches, movement };
}

export function masterQtyFromBalances(
	balances: ConsumableBalanceRow[],
	consumable: Pick<ConsumableRow, "id">,
): string {
	if (consumable.id == null) return "0";
	const total = balancesForConsumable(balances, consumable.id).reduce((sum, row) => sum + parseQty(row.qty), 0);
	return formatQty(total);
}

export function migrateConsumableBalances(
	consumables: ConsumableRow[],
	balances: ConsumableBalanceRow[],
): ConsumableBalanceRow[] {
	const next = [...balances];
	const seeded = new Set(next.map((row) => row.consumableId));
	for (const row of consumables) {
		if (row.id == null || seeded.has(row.id)) continue;
		const qty = parseQty(row.qty);
		if (qty <= 0) continue;
		const loc = legacyLocationFromConsumable(row);
		next.push({
			consumableId: row.id,
			custody: loc.custody,
			customerId: loc.customerId,
			siteId: loc.siteId,
			qty: formatQty(qty),
		});
		seeded.add(row.id);
	}
	return next;
}

function asConsumables(raw: unknown): ConsumableRow[] {
	if (!Array.isArray(raw)) return [];
	return raw.filter((row): row is ConsumableRow => row != null && typeof row === "object") as ConsumableRow[];
}

function asMovements(raw: unknown): ConsumableMovementRow[] {
	if (!Array.isArray(raw)) return [];
	return raw.filter((row): row is ConsumableMovementRow => row != null && typeof row === "object") as ConsumableMovementRow[];
}

function consumableConversions(books: Record<string, unknown>): UnitConversion[] {
	const settings = Array.isArray(books.consumableSettings) ? books.consumableSettings[0] : null;
	if (!settings || typeof settings !== "object") return [];
	const conversions = (settings as { conversions?: UnitConversion[] }).conversions;
	return Array.isArray(conversions) ? conversions : [];
}

function seedLegacyConsumableBalances(books: Record<string, unknown>, consumables: ConsumableRow[]): ConsumableBalanceRow[] {
	const balances = Array.isArray(books.consumableBalances)
		? (books.consumableBalances as ConsumableBalanceRow[])
		: [];
	const next = migrateConsumableBalances(consumables, balances);
	let nextId = 1;
	for (const row of next) {
		if (row.id == null) row.id = nextId++;
		else nextId = Math.max(nextId, Number(row.id) + 1);
	}
	for (const row of consumables) {
		if (row.id == null) continue;
		row.qty = masterQtyFromBalances(next, row);
	}
	books.consumableBalances = next;
	books.consumables = consumables;
	return next;
}

/** Never reuse a consumable id after delete; consider movements and balances still in the audit log. */
export function nextConsumableRecordId(books: Record<string, unknown>): number {
	let max = 0;
	for (const row of asConsumables(books.consumables)) {
		const id = Number(row.id);
		if (Number.isFinite(id)) max = Math.max(max, id);
	}
	for (const row of asMovements(books.consumableMovements)) {
		const id = Number(row.consumableId);
		if (Number.isFinite(id)) max = Math.max(max, id);
	}
	const balances = Array.isArray(books.consumableBalances) ? books.consumableBalances : [];
	for (const row of balances) {
		if (!row || typeof row !== "object") continue;
		const id = Number((row as { consumableId?: unknown }).consumableId);
		if (Number.isFinite(id)) max = Math.max(max, id);
	}
	return max + 1;
}

/** Remove active stock for a consumable; keep movement rows for audit with {@link ConsumableMovementRow.consumableArchived}. */
export function deleteConsumableFromBooks(books: Record<string, unknown>, consumableId: number): void {
	const id = Number(consumableId);
	if (!Number.isFinite(id)) return;
	books.consumables = asConsumables(books.consumables).filter((row) => row.id !== id);
	const balances = Array.isArray(books.consumableBalances)
		? (books.consumableBalances as ConsumableBalanceRow[])
		: [];
	books.consumableBalances = balances.filter((row) => row.consumableId !== id);
	const movements = asMovements(books.consumableMovements);
	books.consumableMovements = movements.map((row) =>
		row.consumableId === id ? { ...row, consumableArchived: true } : row,
	);
	reconcileConsumableStockBooks(books);
}

/** Rebuild location balances and master quantities from the movement audit log. */
export function reconcileConsumableStockBooks(books: Record<string, unknown>): void {
	const consumables = asConsumables(books.consumables);
	const activeIds = new Set(consumables.map((row) => row.id).filter((rowId): rowId is number => rowId != null));
	const movements = asMovements(books.consumableMovements).sort((a, b) => {
		const av = Number(a.id);
		const bv = Number(b.id);
		if (Number.isFinite(av) && Number.isFinite(bv) && av !== bv) return av - bv;
		return String(a.at ?? "").localeCompare(String(b.at ?? ""));
	});
	const conversions = consumableConversions(books);

	if (movements.length === 0) {
		seedLegacyConsumableBalances(books, consumables);
		return;
	}

	let balances: ConsumableBalanceRow[] = [];
	for (const record of movements) {
		if (record.consumableArchived) continue;
		if (!activeIds.has(record.consumableId)) continue;
		const consumable = consumables.find((row) => row.id === record.consumableId);
		if (!consumable?.id) continue;
		const input = movementRecordToInput(record);
		const { balances: next } = applyConsumableMovement(balances, consumable, conversions, input);
		balances = next;
	}

	balances = collapseLegacyExternalBalances(balances);
	let nextId = 1;
	books.consumableBalances = balances.map((row) => ({
		...row,
		id: nextId++,
	}));
	for (const row of consumables) {
		if (row.id == null) continue;
		row.qty = masterQtyFromBalances(balances, row);
	}
	books.consumables = consumables;
}
